import React, { useEffect, useState } from "react";
import { Box, Menu } from "@mui/material";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import WorkbookItemCard from "../../shared/cards/WorkbookItemCard";
import ChartOptionControls from "./ChartOptionControls";
import { useBanner } from "../../context/BannerContext";
import { useChartActions } from "../../hooks/useChartActions";
import { ChartOption, ChartOptionItem } from "../../../excel/charts/api";
import type { ChartCardProps } from "../../../excel/charts/api";
import {
  CHART_DELETE_NOTICE,
  chartOpenError,
  chartOptionUpdateError,
  chartRemoveError,
  chartRemoved,
} from "../../constants";
import ChartIconImage from "./ChartIconImage";
import { deleteChart, getChartDisplay } from "../../../excel/charts/api/operations";

interface GeneratedChartCardProps {
  chart: ChartCardProps;
  onReload: () => Promise<void>;
}

const GeneratedChartCard = ({ chart, onReload }: GeneratedChartCardProps) => {
  const { worksheetName, chartName, ediphiChartType, chartOptions } = chart;
  const { showBanner } = useBanner();
  const { withChartLogging, updateChartOption, activateChart } = useChartActions();

  const [optionValues, setOptionValues] = useState<Record<string, string>>({});
  const [chartType, setChartType] = useState<Excel.ChartType | undefined>();
  const [title, setTitle] = useState(chart.title);
  const [subTitle, setSubTitle] = useState(chart.subTitle);
  /** Non-null while work is in flight; the string names the phase the user is waiting on. */
  const [progress, setProgress] = useState<string | null>(null);
  const [optionsAnchor, setOptionsAnchor] = useState<HTMLElement | null>(null);
  const loading = progress !== null;

  const hasOptions = Object.keys(chartOptions).length > 0;

  // Seeded from what discovery already resolved rather than refetched.
  useEffect(() => {
    setOptionValues(
      // Optional-chained despite the non-optional type: this runs synchronously, so a throw here
      // would take the whole page down.
      Object.fromEntries(
        Object.values(chartOptions).map((option) => [option.key, option.selectedOptionItem?.key ?? ""])
      )
    );
  }, [chartOptions]);

  // The icon and titles reflect the live Excel chart, so they follow a donut/pie, 2D/3D or option change.
  useEffect(() => {
    let isCurrent = true;
    void getChartDisplay(worksheetName, chartName)
      .then((next) => {
        if (!isCurrent) return;
        setChartType(next.chartType);
        setTitle(next.title);
        setSubTitle(next.subTitle);
      })
      .catch(() => {});
    return () => {
      isCurrent = false;
    };
  }, [worksheetName, chartName, optionValues]);

  const handleActivate = async () => {
    if (loading) return;
    try {
      await withChartLogging(async () => activateChart(worksheetName, chartName), {
        context: "GeneratedChartCard_handleActivate",
        chartType: ediphiChartType.key,
        chartName,
      });
    } catch {
      showBanner(chartOpenError(title), "error");
    }
  };

  const handleOptionChange = async (option: ChartOption, optionItem: ChartOptionItem) => {
    const previousValue = optionValues[option.key];
    try {
      const updates = await updateChartOption(worksheetName, chartName, option, optionItem, chartOptions);
      setOptionValues((prev) => ({ ...prev, ...updates }));
    } catch {
      showBanner(chartOptionUpdateError(option.label), "error");
      setOptionValues((prev) => ({ ...prev, [option.key]: previousValue }));
    }
  };

  const handleRemove = async () => {
    setProgress("Removing chart…");
    try {
      await deleteChart(worksheetName, chartName);
      showBanner(chartRemoved(title), "success");
    } catch {
      showBanner(chartRemoveError(title), "error");
    }
    setProgress(null);
    await onReload();
  };

  return (
    <>
      <WorkbookItemCard
        header={{
          text: title,
          name: title,
          subtext: subTitle || worksheetName,
          icon: <ChartIconImage icon={ediphiChartType.chartIconGetter(chartType)} />,
        }}
        itemNoun="chart"
        action={{ onClick: () => void handleActivate(), icon: CenterFocusStrongIcon, tooltip: "Go to chart" }}
        edit={{ onClick: (event) => setOptionsAnchor(event.currentTarget), isVisible: hasOptions }}
        remove={{
          onClick: () => void handleRemove(),
          notice: CHART_DELETE_NOTICE,
        }}
        loading={loading}
        loadingText={progress ?? undefined}
      />
      <Menu
        anchorEl={optionsAnchor}
        open={!!optionsAnchor}
        onClose={() => setOptionsAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ px: 2, py: 1, display: "flex", flexDirection: "column", gap: 2, minWidth: "260px" }}>
          <ChartOptionControls
            options={chartOptions}
            values={optionValues}
            onChange={(option, item) => void handleOptionChange(option, item)}
            disabled={loading}
          />
        </Box>
      </Menu>
    </>
  );
};

export default GeneratedChartCard;
